/**
 * Shared Zod building blocks.
 *
 * These schemas are the single definition of what valid input looks like: the
 * NestJS validation pipe and the React Hook Form resolver both consume them, so
 * a rule can never be enforced on one side and not the other.
 */
import { z } from 'zod';
import { normaliseNigerianPhone } from '../references.js';
import { toKobo, MAX_KOBO } from '../money.js';
export const uuidSchema = z.string().uuid('Not a valid identifier.');
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Email address is required.')
    .max(254, 'Email address is too long.')
    .email('Enter a valid email address.');
/** Accepts any common Nigerian format and stores E.164. */
export const phoneSchema = z
    .string()
    .trim()
    .min(1, 'Phone number is required.')
    .transform((value, ctx) => {
    const normalised = normaliseNigerianPhone(value);
    if (!normalised) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Enter a valid Nigerian phone number, e.g. 0916 848 4980.',
        });
        return z.NEVER;
    }
    return normalised;
});
/**
 * Password policy for a financial system: length does more for security than
 * character-class rules, so the floor is 12 with a light complexity check.
 */
export const passwordSchema = z
    .string()
    .min(12, 'Use at least 12 characters.')
    .max(128, 'Password is too long.')
    .refine((value) => /[a-z]/.test(value), 'Include a lowercase letter.')
    .refine((value) => /[A-Z]/.test(value), 'Include an uppercase letter.')
    .refine((value) => /\d/.test(value), 'Include a number.');
export const nameSchema = z
    .string()
    .trim()
    .min(2, 'Must be at least 2 characters.')
    .max(80, 'Must be 80 characters or fewer.')
    .regex(/^[\p{L}\p{M}'\-. ]+$/u, 'Use letters, spaces, hyphens and apostrophes only.');
export const optionalNameSchema = z
    .union([nameSchema, z.literal('')])
    .optional()
    .transform((value) => (value ? value : null));
/**
 * A naira amount arriving as a string (`"500000.00"`) and leaving as `Kobo`.
 *
 * Strings, not numbers: JSON numbers are IEEE-754 doubles and ₦92,233,720,368
 * is where that stops being merely theoretical.
 */
export const nairaAmountSchema = z
    .union([z.string(), z.number()])
    .transform((value, ctx) => {
    try {
        const kobo = toKobo(value);
        if (kobo < 0n) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Amount cannot be negative.' });
            return z.NEVER;
        }
        if (kobo > MAX_KOBO) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Amount is too large.' });
            return z.NEVER;
        }
        return kobo;
    }
    catch (error) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error instanceof Error ? error.message : 'Enter a valid amount.',
        });
        return z.NEVER;
    }
});
export const positiveNairaAmountSchema = nairaAmountSchema.refine((kobo) => kobo > 0n, 'Amount must be greater than zero.');
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the format YYYY-MM-DD.')
    .transform((value) => new Date(`${value}T00:00:00.000Z`))
    .refine((date) => !Number.isNaN(date.getTime()), 'Not a valid date.');
export const pastDateSchema = isoDateSchema.refine((date) => date <= new Date(), 'Date cannot be in the future.');
/** Members must be adults to enter a loan agreement. */
export const dateOfBirthSchema = isoDateSchema
    .refine((date) => date <= new Date(), 'Date of birth cannot be in the future.')
    .refine((date) => {
    const eighteenYearsAgo = new Date();
    eighteenYearsAgo.setUTCFullYear(eighteenYearsAgo.getUTCFullYear() - 18);
    return date <= eighteenYearsAgo;
}, 'Members must be at least 18 years old.')
    .refine((date) => {
    const hundredTwentyYearsAgo = new Date();
    hundredTwentyYearsAgo.setUTCFullYear(hundredTwentyYearsAgo.getUTCFullYear() - 120);
    return date >= hundredTwentyYearsAgo;
}, 'Enter a valid date of birth.');
/** Nigerian NUBAN account numbers are exactly ten digits. */
export const accountNumberSchema = z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'Nigerian account numbers are 10 digits.');
export const bvnSchema = z.string().trim().regex(/^\d{11}$/, 'A BVN is 11 digits.');
export const membershipNumberSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(3, 'Membership number is too short.')
    .max(32, 'Membership number is too long.')
    .regex(/^[A-Z0-9\-/]+$/, 'Use letters, numbers, hyphens and slashes only.');
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    sortBy: z.string().max(50).optional(),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export const searchSchema = paginationSchema.extend({
    q: z.string().trim().max(120).optional(),
});
export function buildPaginationMeta(page, pageSize, total) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    return {
        page,
        pageSize,
        total,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
    };
}
/** Captured alongside every explicit member confirmation (§17). */
export const consentContextSchema = z.object({
    acceptedTermsVersion: z.string().min(1),
    confirmationText: z.string().min(1).max(2000),
});
//# sourceMappingURL=common.js.map